@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project

import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.example.project.entrys.CUSEntry
import org.example.project.entrys.NETEntry
import org.example.project.entrys.SYSEntry
import org.example.project.manager.LumaticaApiSettings
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.renderComposable

private data class NodeIdentity(
    val id: String,
    val type: String,
    val entry: String,
    val environment: String,
    val port: Int
)

private data class ExpectedNode(
    val type: String,
    val environment: String,
    val port: Int
)

private val domainNodes = mapOf(
    "login.lumatica.de" to ExpectedNode("SYS", "live", 1000),
    "lumatica.de" to ExpectedNode("NET", "live", 2000),
    "www.lumatica.de" to ExpectedNode("NET", "live", 2000)
)

private suspend fun loadNodeIdentity(): NodeIdentity? = runCatching {
    val response = window.fetch("/node/status?entry-check=${js("Date.now()")}").await()
    if (!response.ok) return@runCatching null
    val value = response.json().await().asDynamic()
    val type = (value.typ as? String).orEmpty().trim().uppercase()
    val entry = (value.entry as? String).orEmpty().trim().lowercase()
    val environment = (value.environment as? String).orEmpty().trim().lowercase()
    val port = (value.port as? Number)?.toInt() ?: return@runCatching null
    NodeIdentity((value.id as? String).orEmpty().trim(), type, entry, environment, port)
}.getOrNull()

private fun verifiedNode(identity: NodeIdentity?): NodeIdentity? {
    identity ?: return null
    if (identity.type !in setOf("SYS", "NET", "CUS")) return null
    if (identity.entry != identity.type.lowercase()) return null
    if (identity.environment !in setOf("dev", "live")) return null
    if (identity.port !in 1..65535) return null

    val host = window.location.hostname.lowercase()
    if (window.location.pathname.trimEnd('/') == "/companion" && identity.type in setOf("SYS", "CUS")) return identity
    val expected = domainNodes[host]
    if (expected != null) {
        return identity.takeIf {
            it.type == expected.type &&
                it.environment == expected.environment &&
                it.port == expected.port
        }
    }

    val browserPort = window.location.port.toIntOrNull() ?: return null
    return identity.takeIf { it.port == browserPort }
}

fun main() {
    document.getElementById("root")?.textContent = ""
    if (window.location.pathname == "/api/servicedocumentscreen/share-page") {
        renderComposable(rootElementId = "root") {
            org.example.project.screens_driver.SharedDriverDocumentFolder()
        }
        return
    }

    MainScope().launch {
        if (org.example.project.service.reloadForLatestLoginBuild()) return@launch
        org.example.project.service.installLoginBuildResumeCheck()
        val identity = verifiedNode(loadNodeIdentity())
        org.example.project.dashboard.ScreenManager.applyNodeIdentity(identity?.id.orEmpty())

        renderComposable(rootElementId = "root") {
            org.example.project.dashboard.AppDialogs.Host()
            LaunchedEffect("bootReady") {
                window.setTimeout({
                    try {
                        window.asDynamic().lumaticaBootReady()
                    } catch (_: Throwable) {
                    }
                }, 0)
            }

            when (identity?.type) {
                "SYS" -> SYSEntry()
                "NET" -> NETEntry()
                "CUS" -> {
                    LumaticaApiSettings.nodeIp = window.location.hostname
                    LumaticaApiSettings.nodePort = window.location.port
                    CUSEntry()
                }
                else -> {
                    document.title = "404 – Nicht gefunden"
                    Text("404 – Leider nichts gefunden.")
                }
            }
        }
    }
}
